/**
 * Sound Notifications — owner preferences and local browser persistence.
 *
 * Master mute, volume, voice toggle, severity behaviour, and per-event
 * Off|Beep|Voice choices. Visual notifications are never gated by these
 * settings; sound is additive only.
 */

import { browserStore } from './sections';

export const NOTIFICATION_SOUND_SETTINGS_KEY = 'brunel.command.notification-sound';

export type EventSoundMode = 'off' | 'beep' | 'voice';
export type SeverityBehavior = 'off' | 'beep' | 'voice' | 'beep_and_voice';

export type NotificationEventKey =
  | 'taskFinished'
  | 'taskFailed'
  | 'needsPermission'
  | 'workerOffline'
  | 'workerOnline'
  | 'ownerNeeded';

export type NotificationSeverityKey =
  | 'routine'
  | 'important'
  | 'needsAttention'
  | 'critical';

export interface NotificationSoundSettings {
  readonly masterEnabled: boolean;
  /** 0–1 linear gain applied to every clip. */
  readonly volume: number;
  readonly voiceEnabled: boolean;
  readonly severity: Readonly<Record<NotificationSeverityKey, SeverityBehavior>>;
  readonly events: Readonly<Record<NotificationEventKey, EventSoundMode>>;
}

export const DEFAULT_NOTIFICATION_SOUND_SETTINGS: NotificationSoundSettings = {
  masterEnabled: true,
  volume: 0.7,
  voiceEnabled: true,
  severity: {
    routine: 'beep',
    important: 'beep',
    needsAttention: 'voice',
    critical: 'beep_and_voice',
  },
  events: {
    // Phase 1 defaults from #292
    taskFinished: 'beep',
    taskFailed: 'beep', // attention SFX; voice available via settings
    needsPermission: 'voice',
    workerOffline: 'voice',
    workerOnline: 'beep',
    // #709: a new owner-needed decision/authorization/access step, once per transition.
    ownerNeeded: 'voice',
  },
};

export const NOTIFICATION_EVENT_LABELS: Readonly<Record<NotificationEventKey, string>> = {
  taskFinished: 'Task finished',
  taskFailed: 'Task failed / problem',
  needsPermission: 'Needs permission',
  workerOffline: 'Worker offline',
  workerOnline: 'Worker online / recovered',
  ownerNeeded: 'Owner needs attention',
};

export const NOTIFICATION_SEVERITY_LABELS: Readonly<Record<NotificationSeverityKey, string>> = {
  routine: 'Routine',
  important: 'Important',
  needsAttention: 'Needs attention',
  critical: 'Critical',
};

const EVENT_KEYS: readonly NotificationEventKey[] = [
  'taskFinished', 'taskFailed', 'needsPermission', 'workerOffline', 'workerOnline', 'ownerNeeded',
];

const SEVERITY_KEYS: readonly NotificationSeverityKey[] = [
  'routine', 'important', 'needsAttention', 'critical',
];

const EVENT_MODES: readonly EventSoundMode[] = ['off', 'beep', 'voice'];
const SEVERITY_BEHAVIORS: readonly SeverityBehavior[] = ['off', 'beep', 'voice', 'beep_and_voice'];

interface StringStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

function isEventMode(value: unknown): value is EventSoundMode {
  return typeof value === 'string' && (EVENT_MODES as readonly string[]).includes(value);
}

function isSeverityBehavior(value: unknown): value is SeverityBehavior {
  return typeof value === 'string' && (SEVERITY_BEHAVIORS as readonly string[]).includes(value);
}

function clampVolume(value: unknown): number | undefined {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  return Math.min(1, Math.max(0, value));
}

/** Parse an unknown payload into full settings; unknown fields fall back to defaults. */
export function parseNotificationSoundSettings(raw: unknown): NotificationSoundSettings {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { ...DEFAULT_NOTIFICATION_SOUND_SETTINGS, severity: { ...DEFAULT_NOTIFICATION_SOUND_SETTINGS.severity }, events: { ...DEFAULT_NOTIFICATION_SOUND_SETTINGS.events } };
  }
  const row = raw as Record<string, unknown>;
  const severityIn = (typeof row.severity === 'object' && row.severity !== null && !Array.isArray(row.severity))
    ? row.severity as Record<string, unknown>
    : {};
  const eventsIn = (typeof row.events === 'object' && row.events !== null && !Array.isArray(row.events))
    ? row.events as Record<string, unknown>
    : {};

  const severity = { ...DEFAULT_NOTIFICATION_SOUND_SETTINGS.severity };
  for (const key of SEVERITY_KEYS) {
    if (isSeverityBehavior(severityIn[key])) severity[key] = severityIn[key];
  }
  const events = { ...DEFAULT_NOTIFICATION_SOUND_SETTINGS.events };
  for (const key of EVENT_KEYS) {
    if (isEventMode(eventsIn[key])) events[key] = eventsIn[key];
  }

  return {
    masterEnabled: typeof row.masterEnabled === 'boolean'
      ? row.masterEnabled
      : DEFAULT_NOTIFICATION_SOUND_SETTINGS.masterEnabled,
    volume: clampVolume(row.volume) ?? DEFAULT_NOTIFICATION_SOUND_SETTINGS.volume,
    voiceEnabled: typeof row.voiceEnabled === 'boolean'
      ? row.voiceEnabled
      : DEFAULT_NOTIFICATION_SOUND_SETTINGS.voiceEnabled,
    severity,
    events,
  };
}

export function recallNotificationSoundSettings(store: StringStore | null = browserStore()): NotificationSoundSettings {
  if (store === null) return parseNotificationSoundSettings(null);
  try {
    const raw = store.getItem(NOTIFICATION_SOUND_SETTINGS_KEY);
    if (raw === null || raw === '') return parseNotificationSoundSettings(null);
    return parseNotificationSoundSettings(JSON.parse(raw) as unknown);
  } catch {
    return parseNotificationSoundSettings(null);
  }
}

export function rememberNotificationSoundSettings(
  settings: NotificationSoundSettings,
  store: StringStore | null = browserStore(),
): void {
  if (store === null) return;
  try {
    store.setItem(NOTIFICATION_SOUND_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Storage may be unavailable; keep in-memory only.
  }
}

export function resetNotificationSoundSettings(store: StringStore | null = browserStore()): NotificationSoundSettings {
  const defaults = parseNotificationSoundSettings(null);
  if (store !== null) {
    try {
      store.removeItem?.(NOTIFICATION_SOUND_SETTINGS_KEY);
      if (!store.removeItem) store.setItem(NOTIFICATION_SOUND_SETTINGS_KEY, JSON.stringify(defaults));
    } catch {
      // ignore
    }
  }
  return defaults;
}

export function setMasterEnabled(settings: NotificationSoundSettings, masterEnabled: boolean): NotificationSoundSettings {
  return { ...settings, masterEnabled };
}

export function setNotificationVolume(settings: NotificationSoundSettings, volume: number): NotificationSoundSettings {
  return { ...settings, volume: Math.min(1, Math.max(0, volume)) };
}

export function setVoiceEnabled(settings: NotificationSoundSettings, voiceEnabled: boolean): NotificationSoundSettings {
  return { ...settings, voiceEnabled };
}

export function setEventSoundMode(
  settings: NotificationSoundSettings,
  key: NotificationEventKey,
  mode: EventSoundMode,
): NotificationSoundSettings {
  return { ...settings, events: { ...settings.events, [key]: mode } };
}

export function setSeverityBehavior(
  settings: NotificationSoundSettings,
  key: NotificationSeverityKey,
  behavior: SeverityBehavior,
): NotificationSoundSettings {
  return { ...settings, severity: { ...settings.severity, [key]: behavior } };
}

/** Whether any audible output is allowed right now. */
export function soundAudible(settings: NotificationSoundSettings): boolean {
  return settings.masterEnabled && settings.volume > 0;
}

/**
 * Effective mode for an event after master/voice gates.
 * Returns 'off' when muted or when voice is requested but voice is disabled.
 */
export function effectiveEventMode(
  settings: NotificationSoundSettings,
  key: NotificationEventKey,
): EventSoundMode {
  if (!soundAudible(settings)) return 'off';
  const mode = settings.events[key];
  if (mode === 'voice' && !settings.voiceEnabled) return 'beep';
  return mode;
}
